/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import 'katex/dist/katex.min.css';
import { TextField} from '@mui/material';
import katex from 'katex';
import Page from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import DeleteAlert from '../../../components/modal/DeleteModal';
import SnackBar from '../../../components/snackbar/snackbar';
import ResponsiveTable from '../../../components/Table/Responsivetable';
import QuestionViewModal from '../components/QuestionViewModal';
import adminServices from '../../../services/adminServices';
import useDebounce from '../../../hooks/useDebounce';

const QuestionBank = () => {
  const userId = useSelector((state) => state.userInfo && state.userInfo.id);
  const navigate = useNavigate();
  const [questionList, setQuestionList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [questionType, setQuestionType] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [creatorFilter, setCreatorFilter] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [maxCount, setMaxCount] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarTitle, setSnackbarTitle] = useState('');
  const [loadingQuestionDetails, setLoadingQuestionDetails] = useState(false);
  // Dropdown data states
  const [questionTypes, setQuestionTypes] = useState([]);
  const [questionTypeCodeMap, setQuestionTypeCodeMap] = useState({});
  const [modules, setModules] = useState([]);
  const [creators, setCreators] = useState([]);
  const [createdDateFilter, setCreatedDateFilter] = useState('');
  const [modifiedDateFilter, setModifiedDateFilter] = useState('');

    useEffect(async () => {
          await getAuthors()
      }, []);

       const getAuthors = async (code) => {
              try {
                  const response = await adminServices.getAuthors();
                  if (response.ok) {
                      setCreators(response.data);
                      return true;
                  }
              } catch (error) {
                  console.log(error);
              }
          }
  

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedViewQuestion, setSelectedViewQuestion] = useState(null);

  // Use debounce hook for search
  const debouncedSearchText = useDebounce(searchText, 500);

  const difficultyOptions = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' },
    { value: 'foundation', label: 'Foundation' },
  ];

  // Fetch dropdown data on component mount
  useEffect(() => {
    fetchDropdownData();
  }, []);

  // Fetch all dropdown options
  const fetchDropdownData = async () => {
    try {
      // Fetch question types
      const questionTypeResult = await adminServices.getQuestionTypeDropdown();
      if (questionTypeResult?.data?.data) {
        const codeMapping = {};
        const formattedQuestionTypes = questionTypeResult.data.data
          .filter(item => item.is_active === true)
          .map(item => {
            codeMapping[item.id] = item.code;
            return {
              value: item.code,
            label: item.code
          };
        });
        setQuestionTypes(formattedQuestionTypes);
        setQuestionTypeCodeMap(codeMapping);
      }

      // Fetch assessment types to get modules
      const assessmentTypeResult = await adminServices.getAssessmentTypeDropdown();
      if (assessmentTypeResult?.data?.data && assessmentTypeResult.data.data.filter(item => item.is_active).length > 0) {
        // Fetch modules for the first assessment type or all
        const modulesResult = await adminServices.getModulesDropdown(assessmentTypeResult.data.data.filter(item => item.is_active)[0].id);
        if (modulesResult?.data?.data) {
          const formattedModules = modulesResult.data.data
            .filter(item => item.is_active)
            .map(item => ({
            value: item.id,
            label: item.title || item.name
          }));
          setModules(formattedModules);
        }
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  // Fetch questions
  const getAllQuestions = async () => {
    setLoading(true);
    try {
      const result = await adminServices.getAllQuestion(
        rowsPerPage,
        page - 1,
        debouncedSearchText,
        questionType,
        difficultyFilter,creatorFilter,
        createdDateFilter,
        modifiedDateFilter
      );
      if (result) {
        setMaxCount(result?.data?.total);
        const sortedQuestions = result?.data?.questions.sort((a, b) =>
          new Date(b.created_date) - new Date(a.created_date)
        );
        setQuestionList(sortedQuestions);

        // Extract unique creators
        // const uniqueCreators = [...new Set(sortedQuestions.map(q =>
        //   q.firstname ? `${q.firstname} ${q.lastname}`.trim() : null
        // ))].filter(Boolean);

        // setCreators(uniqueCreators.map(name => ({
        //   value: name,
        //   label: name
        // })));
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setSnackbarTitle('Error fetching questions');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllQuestions();
  }, [page, rowsPerPage, debouncedSearchText, questionType, difficultyFilter, creatorFilter, createdDateFilter, modifiedDateFilter]);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchText, questionType]);

  
  const stripHtml = (html) => {
    if (!html) return '-';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '-';
  };

  const getDifficultyColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'easy':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'hard':
        return 'bg-red-100 text-red-800';
      case 'foundation':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toUpperCase()) {
      case 'MCQ':
        return 'bg-blue-100 text-blue-800';
      case 'MCQ_MULTI':
        return 'bg-purple-100 text-purple-800';
      case 'NUMERIC':
        return 'bg-indigo-100 text-indigo-800';
      case 'PASSAGE_SINGLE':
        return 'bg-pink-100 text-pink-800';
      case 'PASSAGE_MULTI':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderQuillMath = (html) => {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;
    wrapper.querySelectorAll(".ql-formula").forEach((node) => {
      let formula = node.getAttribute("data-value");
      if (!formula) return;
      const textarea = document.createElement("textarea");
      textarea.innerHTML = formula;
      formula = textarea.value;
      formula = formula.replace(/<[^>]+>/g, "");
      try {
        const rendered = katex.renderToString(formula, {
          throwOnError: false,
          strict: false,
          trust: true,
        });
  
        node.outerHTML = rendered;
      } catch {
        node.outerHTML = formula;
      }
    });
  
    return wrapper.innerHTML;
  };
  
  const columns = [
    {
      label: 'S.No',
      key: 'index',
      centered: true,
      render: (row, index) => (
        <span className="text-sm text-gray-900 font-medium">
          {(page - 1) * rowsPerPage + index + 1}
        </span>
      ),
    },
    // {
    //   label: 'Questions',
    //   accessor: row => row.question_text || row.title,
    
    //   render: (row) => {
    //     let html = row.question_text || row.title || "";
    //     html = renderQuillMath(html);
    //     const hasRich =
    //       /<img/i.test(html) ||
    //       /katex/i.test(html) ||
    //       /<sup|<sub/i.test(html);
      
    //     let cleanText = html
    //       .replace(/<sup>(.*?)<\/sup>/gi, "^$1")
    //       .replace(/<sub>(.*?)<\/sub>/gi, "_$1")
    //       .replace(/&nbsp;/g, " ")
    //       .replace(/<[^>]+>/g, "")
    //       .trim();
      
    //     const isTruncated = cleanText.length > 60;
    //     const preview = isTruncated ? cleanText.slice(0, 60) + "..." : cleanText;
    //     return hasRich ? (
    //       <div
    //         title={isTruncated ? cleanText : ""}
    //         className="max-w-md text-sm text-gray-900"
    //         dangerouslySetInnerHTML={{ __html: html }}
    //       />
    //     ) : (
    //       <div
    //         title={isTruncated ? cleanText: ""}
    //         className="max-w-md text-sm text-gray-900">
    //         {preview}
    //       </div>
    //     );
    //   },
    // },
    {
      label: 'Questions',
      accessor: row => row.question_text || row.title,
    
      render: (row) => {
        let html = row.question_text || row.title || "";
        html = renderQuillMath(html);
    
        const hasImage = /<img/i.test(html);
        const hasSupSubKatex = /katex/i.test(html) || /<sup|<sub/i.test(html);
        const hasRich = hasImage || hasSupSubKatex;

        const tmp = document.createElement("div");
        tmp.innerHTML = html;
        const cleanText = (tmp.textContent || tmp.innerText || "")
          .replace(/\s+/g, " ").trim();
        const isTruncated = cleanText.length > 40;
        const previewText = isTruncated
          ? cleanText.slice(0, 40) + "..."
          : cleanText;

      if (hasImage) {
  return (
    <div
      className="max-w-md text-sm text-gray-900"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
        }

        if (hasSupSubKatex) {
          return (
            <div className="relative group max-w-md">
              <div
                className="text-sm text-gray-900 cursor-default line-clamp-1"
                dangerouslySetInnerHTML={{ __html: html }}
              />
              {isTruncated && (
                <div
                  className="absolute z-50 hidden group-hover:block bottom-full left-0 mb-1 px-3 py-2 bg-white text-xs rounded shadow-lg max-w-sm w-max border border-gray-200"
                  style={{ color: '#111827', zIndex: 9999, whiteSpace: 'normal', lineHeight: '1.6' }}
                  dangerouslySetInnerHTML={{ __html: `<span style="color:#111827">${html}</span>` }}
                />
              )}
            </div>
          );
        }
    
        return (
          <div
            title={isTruncated ? cleanText : ""}
            className="max-w-md text-sm text-gray-900"
          >
            {previewText}
          </div>
        );
      },
    },    
    {
      label: 'Type',
      key: 'question_type',
      centered: true,
      render: (row) => (
      <div className="flex justify-center"><span className={`w-48 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 inline-flex items-center justify-center text-center`}>{row.question_type || '-'}
        </span>
        </div>
      ),
    },
    {
      label: 'Difficulty',
      key: 'level',
      centered: true,
      render: (row) => (
     <div className="flex justify-center"><span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize w-24 text-center inline-block ${getDifficultyColor(row.level)}`}>{row.level || '-'}
        </span>
        </div>
      ),
    },
    {
      label: 'Created by',
      key: 'created_by',
      render: (row) => (
        <div>
          <div className="text-sm text-gray-900">{`${row.created_firstname || ''} ${row.created_lastname || ''}`.trim() || '-'}</div>
          <div className="text-xs text-gray-500">
            {row.created_date ? new Date(row.created_date).toLocaleString('en-US', {
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              day: '2-digit', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit', hour12: true
            }) : '-'}
          </div>
        </div>
      ),
    },
    {
      label: 'Last edited by',
      key: 'last_edited_by',
      render: (row) => (
        <div>
          <div className="text-sm text-gray-900">{row.modify_date ? `${row.updated_firstname || ''} ${row.updated_lastname || ''}`.trim() || '-' : 'Never edited'}</div>
          <div className="text-xs text-gray-500">
            {row.modify_date ? new Date(row.modify_date).toLocaleString('en-US', {
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              day: '2-digit', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit', hour12: true
            }) : ''}
          </div>
        </div>
      ),
    },
    // {
    //   label: 'Creator',
    //   key: 'creator',
    //   centered: true,
    //   render: (row) => (
    //     <span className="text-sm text-gray-700">
    //       {`${row.firstname || ''} ${row.lastname || ''}`.trim() || '-'}
    //     </span>
    //   ),
    // },
    // {
    //   label: 'Status',
    //   key: 'is_active',
    //   centered: true,
    //   render: (row) => (
    //   <div className="flex justify-center"><span className={`px-2.5 py-1 rounded-full text-xs font-medium w-24 text-center inline-block ${row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
    //       {row.is_active ? 'Active' : 'Inactive'}
    //     </span>
    //     </div>
    //   ),
    // },
    {
      label: 'Actions',
      key: 'actions',
      centered: true,
      render: (row, index, onActionClick) => (
        <div className="flex items-center justify-center gap-2 w-full">
          <button
            id={`btn-view-question-${row.id}`}
            onClick={() => onActionClick('view', row)}
            className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors duration-150"
            title="View Question"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>

          {row.creator_id === userId && (
            <>
              <button
                id={`btn-edit-question-${row.id}`}
                onClick={() => onActionClick('edit', row)}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                title="Edit Question"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                id={`btn-delete-question-${row.id}`}
                onClick={() => onActionClick('delete', row)}
                className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                title="Delete Question"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const handleActionClick = async (action, question) => {
    switch (action) {
      case 'view':
        setSelectedViewQuestion(null);
        setViewModalOpen(true);

        try {
          const result = await adminServices.getQuestionDetails(question.id);
          if (result?.data) {
            const questionData = Array.isArray(result.data) ? result.data : [result.data];
            setSelectedViewQuestion(questionData);
          } else {
            setSnackbarTitle('Failed to load question details');
            setOpenSnackbar(true);
            setViewModalOpen(false);
          }
        } catch (error) {
          console.error('Error fetching question details:', error);
          setSnackbarTitle('Error loading question details');
          setOpenSnackbar(true);
          setViewModalOpen(false);
        }
        break;
      case 'edit':
        try {
          const result = await adminServices.getQuestionDetails(question.id);
          if (result?.data) {
            const questionData = Array.isArray(result.data) ? result.data : result.data;
            navigate(`/app/question-edit/${question.id}`, {
              state: { questionData }
            });
          } else {
            setSnackbarTitle('Failed to load question details');
            setOpenSnackbar(true);
          }
        } catch (error) {
          console.error('Error fetching question details for edit:', error);
          setSnackbarTitle('Error loading question details');
          setOpenSnackbar(true);
        }
        break;
      case 'delete':
        setSelectedQuestion(question);
        setDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await adminServices.deleteQuestion(selectedQuestion.id);
      if (result.ok) {
        setSnackbarTitle('Question deleted successfully');
        setOpenSnackbar(true);
        setDeleteModalOpen(false);
        getAllQuestions();
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      setSnackbarTitle('Error deleting question');
      setOpenSnackbar(true);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setQuestionType('');
    setDifficultyFilter('');
    setStatusFilter('');
    setModuleFilter('');
    setCreatorFilter('');
    setPage(1)
    setRowsPerPage(10)
  };

  const totalPages = Math.ceil(maxCount / rowsPerPage);

  return (
    <Page title="Question Bank">
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Questions</h2>
              <p className="mt-1 text-sm text-gray-500">Browse and manage your question library</p>
            </div>
            <button
              id="btn-create-question"
              onClick={() => navigate("/app/question-create", { state: { fromChapterPage: false } })} className="inline-flex items-center justify-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors duration-150 shadow-sm"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create Question
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
            <button
              id="btn-clear-filters"
              onClick={handleClearFilters}
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Clear All
            </button>
          </div>
          <div className="grid grid-cols-12 gap-4">

<div className="col-span-12 md:col-span-4">
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Search
  </label>
  <div className="relative">
    <input
      id="input-search-questions"
      type="text"
      placeholder="Search questions..."
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
    />
    <svg
      className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  </div>
</div>

<div className="col-span-12 md:col-span-3">
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Question Type
  </label>
  <select
    id="select-question-type"
    value={questionType}
    onChange={(e) => setQuestionType(e.target.value)}
    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
  >
    <option value="">All Types</option>
    {questionTypes.map((type) => (
      <option
        disabled={type.label === "TEXT"}
        key={type.value}
        value={type.value}
      >
        {type.label}
      </option>
    ))}
  </select>
</div>

<div className="col-span-12 md:col-span-3">
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Difficulty Level
  </label>
  <select
    id="select-difficulty-level"
    value={difficultyFilter}
    onChange={(e) => {
      setDifficultyFilter(e.target.value);
      setPage(1);
      setRowsPerPage(10);
    }}
    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
  >
    <option value="">All Difficulty</option>
    {difficultyOptions
      .filter((opt) => opt.value !== "foundation")
      .map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
  </select>
</div>

<div className="col-span-12 md:col-span-2">
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Page Number
  </label>

  <TextField
    id="input-page-number"
    variant="outlined"
    size="small"
    fullWidth
    value={page}
    onChange={(e) => {
      const val = e.target.value;

      if (val === "" || /^\d+$/.test(val)) {
        const pageNum = val === "" ? 1 : parseInt(val, 10);
        const maxPage = Math.ceil((maxCount || 0) / rowsPerPage);
        const validPage = Math.max(1, Math.min(pageNum, maxPage));
        setPage(validPage);
      }
    }}
    sx={{
      "& .MuiOutlinedInput-root": {
        height: "40px",
        borderRadius: "0.5rem", 
        backgroundColor: "#ffffff",

        "& fieldset": {
          borderColor: "#d1d5db",
        },
        "&:hover fieldset": {
          borderColor: "#6366f1", 
        },
        "&.Mui-focused fieldset": {
          borderColor: "#6366f1",
          borderWidth: "2px",
        },
      },

      "& .MuiInputBase-input": {
        padding: "8px 12px", 
        fontSize: "0.875rem",
      },
    }}
  />
</div>
</div>

<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Content Creator
  </label>
  <select
    id="select-content-creator"
    value={creatorFilter}
    onChange={(e) => {
      setCreatorFilter(e.target.value);
      setPage(1);
      setRowsPerPage(10);
    }}
    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
  >
    <option value="">All Creators</option>
    {creators.map((creator) => (
      <option key={creator.id} value={creator.id}>
        {creator.name}
      </option>
    ))}
  </select>
</div>

<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Created On
  </label>
  <input
    id="input-created-date"
    type="date"
    max={new Date().toISOString().split("T")[0]}
    value={createdDateFilter}
    onChange={(e) => {
      setCreatedDateFilter(e.target.value);
      setPage(1);
    }}
    onKeyDown={(e) => {
      if (/^\d$/.test(e.key)) e.preventDefault();
    }}
    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
  />
</div>

<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Last Edited
  </label>
  <input
    id="input-modified-date"
    type="date"
    max={new Date().toISOString().split("T")[0]}
    value={modifiedDateFilter}
    onChange={(e) => {
      setModifiedDateFilter(e.target.value);
      setPage(1);
    }}
    onKeyDown={(e) => {
      if (/^\d$/.test(e.key)) e.preventDefault();
    }}
    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
  />
</div>

</div>
          {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label htmlFor="search-input" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <input
                  id="search-input"
                  type="text"
                  placeholder="Search questions..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
                <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div>
              <label htmlFor="question-type-select" className="block text-sm font-medium text-gray-700 mb-1">
                Question Type
              </label>
              <select
                id="question-type-select"
                value={questionType}
                onChange={(e) => setQuestionType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="">All Types</option>
                {questionTypes.map((type) => (
                  <option disabled={type.label === 'TEXT'} key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="difficulty-select" className="block text-sm font-medium text-gray-700 mb-1">
                Difficulty Level
              </label>
              <select
                id="difficulty-select"
                value={difficultyFilter}
                onChange={(e) => {setDifficultyFilter(e.target.value);setPage(1);setRowsPerPage(10)}}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="">All Difficulty</option>
                {difficultyOptions
                .filter((opt) => opt.value !== 'foundation')
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="creator-select" className="block text-sm font-medium text-gray-700 mb-1">
                Content Creator
              </label>
              <select
                id="creator-select"
                value={creatorFilter}
                onChange={(e) => {setCreatorFilter(e.target.value);setPage(1);setRowsPerPage(10)}}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="">All Creators</option>
                {creators.map((creator) => (
                  <option key={creator.name} value={creator.id}>
                    {creator.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Created On</label>
              <input
                type="date"  max={new Date().toISOString().split("T")[0]} 
                value={createdDateFilter}
                onChange={(e) => { setCreatedDateFilter(e.target.value); setPage(1); }}
                onKeyDown={(e) => {if (/^\d$/.test(e.key)) e.preventDefault();}}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Edited</label>
              <input
                type="date"  max={new Date().toISOString().split("T")[0]} 
                value={modifiedDateFilter}
                onChange={(e) => { setModifiedDateFilter(e.target.value); setPage(1); }}
                onKeyDown={(e) => {if (/^\d$/.test(e.key)) e.preventDefault();}}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
          </div> */}
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <ResponsiveTable
            columns={columns}
            data={questionList}
            loading={loading}
            onActionClick={handleActionClick}
          />

          {questionList && questionList?.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(page - 1) * rowsPerPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(page * rowsPerPage, maxCount)}
                  </span>{' '}
                  of <span className="font-medium">{maxCount}</span> results
                </div>

                <div className="flex items-center gap-2">
                  <select
                    id="select-rows-per-page"
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>

                  <button
                    id="btn-pagination-previous"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                  >
                    Previous
                  </button>

                  <div className="hidden sm:flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }

                      return (
                        <button
                          key={i}
                          id={`btn-page-${pageNum}`}
                          onClick={() => setPage(pageNum)}
                          className={`px-3 py-1.5 border rounded-lg text-sm font-medium transition-colors duration-150 ${page === pageNum
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    id="btn-pagination-next"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <SnackBar
        open={openSnackbar}
        snackbarTitle={snackbarTitle}
        close={() => setOpenSnackbar(false)}
      />

      {deleteModalOpen && (
        <DeleteAlert
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Question"
          message="Are you sure you want to delete this question? This action cannot be undone."
        />
      )}
      {viewModalOpen && (
        <QuestionViewModal
          open={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedViewQuestion(null);
            getAllQuestions()
          }}
          questions={selectedViewQuestion}
          questionTypeCode={
            Array.isArray(selectedViewQuestion) && selectedViewQuestion.length > 0
              ? selectedViewQuestion[0].question_type_description
              : selectedViewQuestion?.question_type_description
          }
        />
      )}

    </Page>
  );
};

export default QuestionBank;